import { useState, useEffect } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Eye, EyeOff } from "lucide-react";
import { BrandMark } from "@/components/Layout";
import { useAuth, formatApiError } from "@/context/AuthContext";
import { PHONE_COUNTRIES, buildIntlPhone } from "@/lib/phone";

const HERO = "https://images.unsplash.com/photo-1419242902214-272b3f66ee7a?auto=format&fit=crop&w=1200&q=70";

export const spaceFor = (u) => (u && (u.role === "medium" || u.role === "admin") ? "/medium" : "/espace");

// Destination après connexion : honore ?next=/... pour les clients (ex. lien "laisser un avis")
const destFor = (u, search) => {
  const staff = u && (u.role === "medium" || u.role === "admin");
  if (!staff && search) {
    const next = new URLSearchParams(search).get("next");
    if (next && next.startsWith("/") && !next.startsWith("//")) return next;
  }
  return spaceFor(u);
};

export const AuthShell = ({ title, subtitle, children }) => (
  <div className="min-h-screen grid lg:grid-cols-2">
    <div className="hidden lg:block relative overflow-hidden night">
      <img src={HERO} alt="" className="w-full h-full object-cover opacity-70" />
      <div className="absolute inset-0" style={{ background: "linear-gradient(to top, #171334, rgba(23,19,52,0.3))" }} />
      <div className="absolute bottom-16 left-12 max-w-md">
        <p className="font-mono text-xs tracking-[0.3em] text-goldsoft mb-4">VOYANCE ÉTOILÉE</p>
        <h2 className="font-serif text-5xl leading-tight text-[#f5f3ff]">Sous les étoiles, éclairez votre chemin.</h2>
      </div>
    </div>
    <div className="flex items-center justify-center px-6 py-16">
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }} className="w-full max-w-md">
        <Link to="/" className="inline-block mb-12"><BrandMark /></Link>
        <h1 className="font-serif text-4xl mb-2">{title}</h1>
        <p className="text-[var(--muted)] mb-10">{subtitle}</p>
        {children}
      </motion.div>
    </div>
  </div>
);

// Champ réutilisable — pour les mots de passe, ajoute un œil pour afficher/masquer la saisie.
export const Field = ({ label, type, ...props }) => {
  const [show, setShow] = useState(false);
  const isPw = type === "password";
  const tid = props["data-testid"];
  return (
    <label className="block mb-5">
      <span className="text-xs font-mono tracking-[0.15em] text-[var(--muted)]">{label}</span>
      <div className="relative mt-2">
        <input {...props} type={isPw && show ? "text" : type}
          className={`w-full bg-white border border-[var(--border)] rounded-xl px-4 py-3 ${isPw ? "pr-12" : ""} text-[var(--text)] focus:border-gold outline-none transition-colors`} />
        {isPw && (
          <button type="button" tabIndex={-1} onClick={() => setShow((s) => !s)}
            data-testid={tid ? `${tid}-toggle` : "password-toggle"}
            aria-label={show ? "Masquer le mot de passe" : "Afficher le mot de passe"}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-[var(--muted)] hover:text-gold transition-colors">
            {show ? <EyeOff className="w-5 h-5" strokeWidth={1.6} /> : <Eye className="w-5 h-5" strokeWidth={1.6} />}
          </button>
        )}
      </div>
    </label>
  );
};

export function LoginForm({ title = "Connexion", subtitle = "Retrouvez votre espace et vos consultations.", isMedium = false }) {
  const { user, login, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => { if (user) navigate(destFor(user, location.search), { replace: true }); }, [user, navigate, location.search]);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const u = await login(email, password);
      const staff = u.role === "medium" || u.role === "admin";
      if (isMedium && !staff) {
        await logout();
        toast.error("Cet espace est réservé aux médiums. Utilisez la connexion client.");
        return;
      }
      if (!isMedium && staff) {
        await logout();
        toast.error("Compte médium : merci d'utiliser l'accès expert (pied de page).");
        return;
      }
      toast.success("Bienvenue !");
      navigate(destFor(u, location.search), { replace: true });
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || err.message); }
    finally { setLoading(false); }
  };
  return (
    <AuthShell title={title} subtitle={subtitle}>
      {isMedium && <div className="mb-6 inline-flex items-center gap-2 text-xs font-mono tracking-wider text-[var(--violet)] bg-[var(--violet)]/10 border border-[var(--violet)]/20 rounded-full px-3 py-1.5" data-testid="medium-badge">ESPACE PROFESSIONNEL · MÉDIUMS</div>}
      <form onSubmit={submit} data-testid="login-form">
        <Field label="EMAIL" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} data-testid="login-email" />
        <Field label="MOT DE PASSE" type="password" required value={password} onChange={(e) => setPassword(e.target.value)} data-testid="login-password" />
        <div className="text-right -mt-3 mb-4">
          <Link to="/mot-de-passe-oublie" data-testid="forgot-password-link" className="text-[var(--muted)] text-xs hover:text-gold link-underline">Mot de passe oublié ?</Link>
        </div>
        <button disabled={loading} data-testid="login-submit" className="btn-gold w-full py-3.5 rounded-full text-sm font-medium mt-2">{loading ? "..." : "Se connecter"}</button>
      </form>
      {isMedium ? (
        <p className="text-[var(--muted)] text-sm mt-8">Vous êtes client(e) ? <Link to="/connexion" className="text-gold link-underline">Connexion client</Link></p>
      ) : (
        <>
          <p className="text-[var(--muted)] text-sm mt-8">Pas encore de compte ? <Link to="/inscription" className="text-gold link-underline">Créer un compte</Link></p>
          <p className="text-[var(--muted)] text-xs mt-3">Vous êtes médium ? <Link to="/acces-medium" data-testid="medium-access-link" className="text-[var(--violet)] link-underline">Accès expert médium</Link></p>
        </>
      )}
    </AuthShell>
  );
}

export function RegisterForm() {
  const { user, register } = useAuth();
  const navigate = useNavigate();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [phoneCountry, setPhoneCountry] = useState("+33");
  const [birthdate, setBirthdate] = useState("");
  const [password, setPassword] = useState("");
  const [acceptTerms, setAcceptTerms] = useState(false);
  const [certifAdult, setCertifAdult] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => { if (user) navigate(spaceFor(user), { replace: true }); }, [user, navigate]);

  const ageFrom = (d) => {
    if (!d) return null;
    const b = new Date(d), t = new Date();
    let a = t.getFullYear() - b.getFullYear();
    const m = t.getMonth() - b.getMonth();
    if (m < 0 || (m === 0 && t.getDate() < b.getDate())) a--;
    return a;
  };

  const submit = async (e) => {
    e.preventDefault();
    const age = ageFrom(birthdate);
    if (age === null || age < 18) {
      toast.error("Vous devez avoir 18 ans ou plus pour créer un compte.");
      return;
    }
    if (!acceptTerms) {
      toast.error("Vous devez lire et accepter les Conditions Générales pour continuer.");
      return;
    }
    if (!certifAdult) {
      toast.error("Vous devez certifier avoir plus de 18 ans pour créer un compte.");
      return;
    }
    setLoading(true);
    try { const u = await register(name, email, password, buildIntlPhone(phoneCountry, phone), birthdate, acceptTerms); toast.success("Compte créé !"); navigate(spaceFor(u), { replace: true }); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail) || err.message); }
    finally { setLoading(false); }
  };
  const maxDate = new Date().toISOString().split("T")[0];
  return (
    <AuthShell title="Créer mon compte" subtitle="Inscription rapide, gratuite et confidentielle.">
      <form onSubmit={submit} data-testid="register-form">
        <Field label="PRÉNOM" type="text" required value={name} onChange={(e) => setName(e.target.value)} data-testid="register-name" />
        <Field label="EMAIL" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} data-testid="register-email" />
        <label className="block mb-5">
          <span className="text-xs font-mono tracking-[0.15em] text-[var(--muted)]">TÉLÉPHONE</span>
          <div className="flex gap-2 mt-2">
            <select data-testid="register-phone-country" value={phoneCountry} onChange={(e) => setPhoneCountry(e.target.value)}
              className="bg-white border border-[var(--border)] rounded-xl px-2 py-3 text-sm text-[var(--text)] focus:border-gold outline-none transition-colors">
              {PHONE_COUNTRIES.map((c) => <option key={c.code} value={c.code}>{c.label} ({c.code})</option>)}
            </select>
            <input type="tel" required placeholder="6 12 34 56 78" value={phone} onChange={(e) => setPhone(e.target.value)} data-testid="register-phone"
              className="flex-1 min-w-0 bg-white border border-[var(--border)] rounded-xl px-4 py-3 text-[var(--text)] focus:border-gold outline-none transition-colors" />
          </div>
          <span className="text-[11px] text-[var(--muted)] mt-1.5 block">Choisissez votre pays (France, Belgique, Suisse…) puis tapez votre numéro sans le 0 initial.</span>
        </label>
        <Field label="DATE DE NAISSANCE" type="date" required max={maxDate} value={birthdate} onChange={(e) => setBirthdate(e.target.value)} data-testid="register-birthdate" />
        <p className="text-xs text-[var(--muted)] -mt-3 mb-5">Réservé aux personnes majeures (18 ans et plus).</p>
        <Field label="MOT DE PASSE" type="password" required minLength={6} value={password} onChange={(e) => setPassword(e.target.value)} data-testid="register-password" />
        <label className="flex items-start gap-3 mb-5 cursor-pointer" data-testid="register-terms-label">
          <input type="checkbox" checked={acceptTerms} onChange={(e) => setAcceptTerms(e.target.checked)} data-testid="register-terms"
            className="mt-0.5 w-4 h-4 accent-[#d4af37] shrink-0" />
          <span className="text-xs text-[var(--muted)] leading-relaxed">
            J'ai lu et j'accepte les <Link to="/conditions-generales" target="_blank" className="text-gold hover:underline">Conditions Générales d'Utilisation et de Vente</Link> de Voyance Étoilée.
          </span>
        </label>
        <label className="flex items-start gap-3 mb-5 cursor-pointer" data-testid="register-adult-label">
          <input type="checkbox" checked={certifAdult} onChange={(e) => setCertifAdult(e.target.checked)} data-testid="register-adult"
            className="mt-0.5 w-4 h-4 accent-[#d4af37] shrink-0" />
          <span className="text-xs text-[var(--muted)] leading-relaxed">Je certifie avoir plus de 18 ans.</span>
        </label>
        <button disabled={loading} data-testid="register-submit" className="btn-gold w-full py-3.5 rounded-full text-sm font-medium mt-2">{loading ? "..." : "Créer mon compte"}</button>
      </form>
      <p className="text-[var(--muted)] text-sm mt-8">Déjà inscrit(e) ? <Link to="/connexion" className="text-gold link-underline">Se connecter</Link></p>
    </AuthShell>
  );
}
